import {GetMapObjects} from "../../../Server/DB/mapObjects.js";
import {GetAsync, observer_mgl} from "mobx-graphlink";
import moment from "moment";
import React, {useRef, useState} from "react";
import {Button, CheckBox, Column, Row, Select, Spinner, Text} from "react-vcomponents";
import {InfoButton, RunInAction_Set, Slider, TextPlus} from "web-vcore";
import {DMap} from "../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../Store/main/maps/@MapState.js";
import {AnchorDisplayLayer, MapSettings_GPU, MapSettingsTab, SizeReferenceEntry, ToneMappingMode} from "../../../Store/main/maps/@MapState/MapSettings.js";
import type {ComponentBackend} from "../../../Terrain/Heights/Poisson/Solve/Solve_Common.js";
import {TexturePicker} from "../../../Utils/ReactComponents/TexturePicker.js";
import {Vec2} from "../../../Utils/ThreeJS/VectorStructs.js";
import {GetEntries, StartDownload} from "js-vextensions";
import {store} from "../../../Store/index.js";
import {ScrollView} from "react-vscrollview";
import {GetCurrentMapControls} from "../../../Store/main/maps.js";
import {DEFAULT_SHADOW_BIAS, DEFAULT_SHADOW_MAP_SIZE, DEFAULT_SHADOW_RADIUS, resolveShadowBias, resolveShadowMapSize, resolveShadowRadius} from "../../../Terrain/Heights/TerrainShadows.js";
import {ShowImportFromFileDialog} from "./ImportFromFileDialog.js";
import {DEFAULT_MAX_VIEW_DISTANCE, DEFAULT_SCENERY_MAX_DISTANCE} from "../../../Terrain/Heights/TerrainViewInfo.js";

/** Set14 P7 §4.11 — the tone-mapping selector options (readable names over the numeric three
 *  constants). CustomToneMapping (5) is deliberately excluded (it needs a caller-supplied
 *  toneMappingFn). */
const TONE_MAPPING_OPTIONS: {name: string; value: number}[] = [
	{name: "None (pre-P7)", value: ToneMappingMode.NoToneMapping},
	{name: "Linear", value: ToneMappingMode.LinearToneMapping},
	{name: "Reinhard", value: ToneMappingMode.ReinhardToneMapping},
	{name: "Cineon", value: ToneMappingMode.CineonToneMapping},
	{name: "ACES filmic (default)", value: ToneMappingMode.ACESFilmicToneMapping},
	{name: "AgX", value: ToneMappingMode.AgXToneMapping},
	{name: "Neutral", value: ToneMappingMode.NeutralToneMapping},
];

export const SettingsUI = observer_mgl((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const {settings} = mapState;

	return (
		<Column style={{flex: 1}}>
			<Row mt={5} mb={5}>
				<Select displayType="button bar" options={GetEntries(MapSettingsTab, "ui")}
					value={settings.tab} onChange={val=>RunInAction_Set(()=>settings.tab = val)}/>
			</Row>
			<ScrollView>
				{settings.tab == MapSettingsTab.general && <GeneralSubpanel {...{map, mapState}}/>}
				{settings.tab == MapSettingsTab.terrain && <TerrainSubpanel {...{map, mapState}}/>}
				{settings.tab == MapSettingsTab.gpu && <GPUSubpanel {...{map, mapState}}/>}
				{settings.tab == MapSettingsTab.view3D && <View3DSubpanel {...{map, mapState}}/>}
			</ScrollView>
		</Column>
	);
});

const GeneralSubpanel = observer_mgl((props: {map: DMap, mapState: MapState})=>{
	const [showPasteInput, setShowPasteInput] = useState(false);
	const pasteInputRef = useRef<HTMLInputElement>(null);
	const {map, mapState} = props;
	const general = mapState.settings.general;
	const refs = general.references ?? [];
	const mapControls = GetCurrentMapControls();
	const changeZoom = (zoomLevelDelta: number, roundTo: number)=>{
		if (!mapControls) return;
		const newZoomLevel = (mapControls.zoomLevel + zoomLevelDelta).RoundTo(roundTo);
		mapControls.setZoom(newZoomLevel);
	};

	return (
		<>
			<Row>
				<Text>Actions:</Text>
				<Button ml={5} text="Export all map-objects" onClick={async()=>{
					const mapObjects = await GetAsync(()=>GetMapObjects(map.id));
					const output = JSON.stringify({
						map,
						mapObjects,
					}, null, "\t");
					StartDownload(output, `MapExport_${moment().format("YYYY-MM-DD-HH-mm-ss")}_${map.name}.json`);
				}}/>
				<Button ml={5} text="Import from file" onClick={()=>ShowImportFromFileDialog({map, mapState})}/>
			</Row>
			<Row mt={5}>
				<Text>View:</Text>
				<Button ml={5} mdIcon="content-copy" title="Copy view (center + zoom)" onClick={()=>{
					const viewState = {center: {x: mapState.camPos_0ms.x, y: mapState.camPos_0ms.y}, zoom: mapState.camZoom_0ms};
					navigator.clipboard.writeText(JSON.stringify(viewState));
				}}/>
				<Button ml={5} mdIcon="content-paste" title="Paste/apply view (center + zoom)" onClick={()=>{
					setShowPasteInput(true);
					setTimeout(()=>pasteInputRef.current?.focus(), 0);
				}}/>
				{showPasteInput && <input ref={pasteInputRef} placeholder="Paste view JSON here (Ctrl+V)" style={{marginLeft: 5, width: 220}}
					onPaste={e=>{
						const text = e.clipboardData.getData("text");
						try {
							const viewState = JSON.parse(text) as {
								center?: {x: number, y: number}, zoom?: number, // simple version
								minX?: number, minY?: number, maxX?: number, maxY?: number, // precise version
							};
							if (viewState.minX != null && viewState.minY != null && viewState.maxX != null && viewState.maxY != null) {
								// Bbox form: fit the camera to [minX..maxX]×[minY..maxY] and center it.
								const bboxW = Math.abs(viewState.maxX - viewState.minX);
								const bboxH = Math.abs(viewState.maxY - viewState.minY);
								const center = viewState.center ?? {x: (viewState.minX + viewState.maxX) / 2, y: (viewState.minY + viewState.maxY) / 2};
								if (mapControls && bboxW > 0 && bboxH > 0) {
									const rect = mapControls.DOM?.clientRect;
									if (rect) {
										const targetPxPerMeter = 0.9 * Math.max(rect.width / bboxW, rect.height / bboxH);
										if (targetPxPerMeter > 0 && Number.isFinite(targetPxPerMeter)) {
											mapControls.panTo(new Vec2(center.x, center.y));
											mapControls.setZoom(mapControls.UntransformTransformedZoomLevel(targetPxPerMeter));
										}
									}
								}
							} else if (viewState.center && viewState.zoom != null) {
								// Original center+zoom form.
								mapControls?.panTo(new Vec2(viewState.center.x, viewState.center.y));
								mapControls?.setZoom(viewState.zoom);
							}
						} catch {}
						setShowPasteInput(false);
					}}
					onBlur={()=>setShowPasteInput(false)}
				/>}
			</Row>
			<Row mt={5}>
				<Text>Zoom:</Text>
				<Spinner ml={5} style={{width: 80}} min={-Infinity} step={0.5} instant
					value={mapState.camZoom_0ms} onChange={val=>mapControls?.setZoom(val)}/>
				<Button ml={5} text="-0.5" onClick={()=>changeZoom(-.5, .5)}/>
				<Button ml={5} text="+0.5" onClick={()=>changeZoom(0.5, .5)}/>
				<Button ml={5} text="-0.1" onClick={()=>changeZoom(-.1, .1)}/>
				<Button ml={5} text="+0.1" onClick={()=>changeZoom(0.1, .1)}/>
			</Row>
			<Row>
				<CheckBox text="Screenshot mode" value={general.screenshotMode} onChange={val=>RunInAction_Set(()=>general.screenshotMode = val)}/>
				<InfoButton ml={5} text={`
					Screenshot-mode affects the map display/rendering in quite a few ways. This is not (yet) exhaustive, but here are some of the effects:
					* Hides the anchor-entity icons on map surface. (even if "Show anchors" is enabled)
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="Preserve drawing buffer (debug)" value={general.debug_preserveDrawingBuffer}
					onChange={val=>RunInAction_Set(()=>general.debug_preserveDrawingBuffer = val)}/>
				<InfoButton ml={5} text={`
					Debug aid: keeps the WebGL drawing buffer after each frame, so the agent/devtools can read actual rendered pixels via readPixels (instead of getting all-zeroes). Perf-neutral to leave off in production.
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="Focus L1 size, X:" value={general.focusL1_enabled} onChange={val=>RunInAction_Set(()=>general.focusL1_enabled = val)}/>
				<Spinner ml={5} style={{width: 70}} value={general.focusL1_size?.x} onChange={val=>RunInAction_Set(()=>general.focusL1_size = new Vec2(val, general.focusL1_size?.y ?? 0))}/>
				<Text ml={5}>Y:</Text>
				<Spinner ml={5} style={{width: 70}} value={general.focusL1_size?.y} onChange={val=>RunInAction_Set(()=>general.focusL1_size = new Vec2(general.focusL1_size?.x ?? 0, val))}/>
				<CheckBox ml={5} text="Show bounds" title="Note: These bounds are hidden when screenshot-mode is enabled." value={general.focusL1_show} onChange={val=>RunInAction_Set(()=>general.focusL1_show = val)}/>
				<CheckBox ml={5} text="Lock" value={general.focusL1_lockPos != null} onChange={val=>{
					RunInAction_Set(()=>general.focusL1_lockPos = val ? mapState.camPos_0ms.ToVec2() : null);
				}}/>
			</Row>
			<Row mt={5}>
				<Text>Show:</Text>
				<CheckBox ml={5} text="Labels" value={general.showLabels} onChange={val=>RunInAction_Set(()=>general.showLabels = val)}/>
				<CheckBox ml={5} text="Shapes" value={general.showShapes} onChange={val=>RunInAction_Set(()=>general.showShapes = val)}/>
				<CheckBox ml={5} text="Guide-grids" value={general.showGuideGrids} onChange={val=>RunInAction_Set(()=>general.showGuideGrids = val)}/>
				<CheckBox ml={5} text="Viewpoints" value={general.showViewpoints} onChange={val=>RunInAction_Set(()=>general.showViewpoints = val)}/>
			</Row>
			<Row mt={5}>
				<Text>Viewpoint projection max-distance:</Text>
				<Spinner ml={5} style={{width: 70}} value={general.viewpoints_maxProjectionDistance}
					onChange2={val=>RunInAction_Set(()=>general.viewpoints_maxProjectionDistance = val)}/>
				<InfoButton ml={5} text={`
					When set (>0), the viewpoint-projection overlay clamps each viewpoint wedge's reach to this many metres from the viewpoint's eye position.
					Useful for cameras with a pitch of 0 (looking at the horizon), whose ground reach would otherwise extend to (near-)infinity.
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={10}>
				<CheckBox text="Show anchors" value={general.showAnchors} onChange={val=>RunInAction_Set(()=>general.showAnchors = val)}/>
				<Spinner ml={5} style={{width: 50}} value={general.anchors_historyDepth} onChange={val=>RunInAction_Set(()=>general.anchors_historyDepth = val)}/>
				<TextPlus ml={5} info={`
					* Low: Only above the terrain and map-objects themselves.
					* Mid: Also above map-object "shapes".
					* High: Also above map-object labels.
				`.AsMultiline(0)}>Layer:</TextPlus>
				<Select ml={5} displayType="button bar" options={GetEntries(AnchorDisplayLayer)}
					value={general.anchors_layer} onChange={val=>RunInAction_Set(()=>general.anchors_layer = val)}/>
			</Row>
			<Row mt={5}>
				<Text>Size:</Text>
				<Row ml={5} mr={10} style={{flex: 1}}>
					<Slider ml={5} min={0} max={.1} step={0.01} //{...{style: {track: {marginRight: 30}}} as any}
						value={general.anchors_size ?? 1} onChange={val=>RunInAction_Set(()=>general.anchors_size = val)}/>
				</Row>
			</Row>
			<Row mt={10}>
				<CheckBox text="Show references" value={general.showReferences} onChange={val=>RunInAction_Set(()=>general.showReferences = val)}/>
				<Row ml={5} mr={10} style={{flex: 1}}>
					<Slider ml={5} min={0} max={1} step={0.01} //{...{style: {track: {marginRight: 30}}} as any}
						value={general.references_opacity ?? 1} onChange={val=>RunInAction_Set(()=>general.references_opacity = val)}/>
				</Row>
			</Row>
			<Row mt={5}>
				<TextPlus>References:</TextPlus>
				<Button ml={5} mdIcon="plus" onClick={()=>{
					const newRef = new SizeReferenceEntry({});
					RunInAction_Set(()=>general.references = (general.references ?? []).concat(newRef));
				}}/>
			</Row>
			{refs.map((ref, index)=>{
				const ChangeRef = (refChanger: (ref: SizeReferenceEntry)=>any)=>{
					return RunInAction_Set(()=>refChanger(refs[index]));
				};
				return (
					<Row key={index} mt={5} style={{background: "rgba(255,255,255,.2)", padding: 5, borderRadius: 5}}>
						<CheckBox text="Enabled" value={ref.enabled} onChange={val=>ChangeRef(a=>a.enabled = val)}/>
						<TexturePicker ml={5} requiredEntityTags={["refs"]} value={ref.textureId} onChange={val=>ChangeRef(a=>a.textureId = val)}/>
						<Button ml="auto" mdIcon="delete" onClick={()=>{
							RunInAction_Set(()=>general.references = refs.filter((_, i)=>i !== index));
						}}/>
					</Row>
				);
			})}
		</>
	);
});

const TerrainSubpanel = observer_mgl((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const terrain = mapState.settings.terrain;
	return (
		<>
			{/* ── Scenery ───────────────────────────────────────────────── */}
			<Row mt={5}>
				<CheckBox text="Show scenery" value={terrain.showScenery} onChange={val=>RunInAction_Set(()=>terrain.showScenery = val)}/>
			</Row>
			{/* ── Terrain elevation overlay ─────────────────────────────── */}
			<Row mt={5}>
				<CheckBox text="Terrain elevation overlay" value={terrain.overlay_enabled}
					onChange={val=>RunInAction_Set(()=>terrain.overlay_enabled = val)}/>
			</Row>
			{/* ── Height solver toggle (2026-08-24) ─────────────────────── */}
			<Row mt={5}>
				<CheckBox text="Solve terrain heights" value={terrain.overlay_heightSolverEnabled}
					onChange={val=>RunInAction_Set(()=>terrain.overlay_heightSolverEnabled = val)}/>
				<InfoButton ml={5} text={`
					When ON (default), terrain heights are solved through the Poisson height-solver
					system (worker prepareTerrain/solveTerrain). When OFF, heights are NOT solved — every
					chunk renders at the hard-coded fallback height below instead. This makes the terrain
					MATERIALS appear near-instantly after a pan/zoom, which is ideal when working
					exclusively in 2D and caring only about the materials, not the elevation.
				`.AsMultiline(0)}/>
				<TextPlus ml={5} info={`
					The global terrain height (meters) used when "Solve terrain heights" is OFF — every
					chunk's height lattice is filled with this constant. Default 0 (sea level).
				`.AsMultiline(0)}>Fallback:</TextPlus>
				<Spinner ml={5} style={{width: 80}}
					value={terrain.overlay_heightFallback} onChange={val=>RunInAction_Set(()=>terrain.overlay_heightFallback = val)}/>
				<Text ml={5}>m</Text>
			</Row>
			<Row mt={5}>
				<CheckBox text="Procedural materials" value={terrain.proceduralMaterials}
					onChange={val=>RunInAction_Set(()=>terrain.proceduralMaterials = val)}/>
				<InfoButton ml={5} text={`
					Enables procedural materials. (fragment-shader surfacing)
				`.AsMultiline(0)}/>
				{/* 2026-08-25 — DEBUG SHIM: replace the (huge, SwiftShader-hostile) real material
				    shaders with a single trivial hashed-colour shim so regions are still visually
				    distinguishable without the compile/freeze wall in vscode's integrated browser. */}
				<CheckBox ml={10} text="Debug"
					value={terrain.debugShimMats} onChange={val=>RunInAction_Set(()=>terrain.debugShimMats = val)}/>
				<InfoButton ml={5} text={`
					Enables the debug shader shim. (for debugging, or no-gpu browsers)
					Note: There appears to be a glitch with this checkbox atm... disable then re-enable to apply it.
				`.AsMultiline(0)}/>
			</Row>
			{terrain.proceduralMaterials && <>
				<Row mt={5}>
					<TextPlus info={`
						Set9 Phase 5 (experimental): renders terrain with a world-space fragment shader
						instead of the CPU-baked 33×33 albedo bitmap. No tiling by construction; hillshade
						+ hypsometric ramp now run in the shader. A splat field (worker-rasterised from
						elevation/slope rules) selects up to 4 materials per chunk — the demo set is
						dirt (base) + cobblestone (high elevation) + snow (peaks). Toggle off to return
						to the legacy bake (one setting away during bring-up, per the Set9 plan).
					`.AsMultiline(0)}>Note:</TextPlus>
				</Row>
			</>}
			{/* ── Set14 P7 §4.11 — output transfer chain (tone mapping) ────── */}
			<Row mt={5}>
				<TextPlus info={`
					Set14 P7 — the tone-mapping mode applied to the terrain + scenery OUTPUT chain
					(tonemap + sRGB-encode into the renderer's output space). This closes the
					tree↔terrain brightness/contrast gap opened in P4: trees (baked with
					MeshStandardMaterial) are already on the chain; the terrain fragment was raw.
					Changing this re-bakes the scenery atlases (toneMapping joins the bake signature).

					Mode 0 (No tone mapping) = the pre-P7 terrain look exactly (with a linear output
					space, the sRGB encode is the identity). Mode 4 (ACES filmic) = the default — the
					terrain system was TUNED for this look, so other modes are for experimentation.
					CustomToneMapping is intentionally absent (it needs a caller-supplied function).
				`.AsMultiline(0)}>Tone mapping:</TextPlus>
				<Select displayType="button bar" ml={5}
					options={TONE_MAPPING_OPTIONS}
					value={terrain.toneMapping}
					onChange={val=>RunInAction_Set(()=>terrain.toneMapping = val as ToneMappingMode)}/>
			</Row>
			{terrain.overlay_enabled && <>
				<Row mt={5}>
					<TextPlus info={`
						Number of web workers used for terrain tile generation. More workers can speed up tile generation on multi-core CPUs.
						Changing this value restarts the worker pool. (BUG: causes black tiles atm; leave page and return to fix)
						NOTE: Currently, more workers actually slows generation down a lot! Until resolved, 1 worker is highly recommended.
					`.AsMultiline(0)}>Terrain worker count:</TextPlus>
					<Spinner ml={5} style={{width: 60}} min={1} max={16} step={1}
						value={terrain.overlay_workerCount} onChange={val=>RunInAction_Set(()=>terrain.overlay_workerCount = val)}/>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Number of terrain chunks packed into each worker message (amortises per-message overhead across N chunks).
						Recommended: 16 or 32 (empirical sweet spot on test machine; too high regresses).
					`.AsMultiline(0)}>Eval chunk batch size:</TextPlus>
					<Spinner ml={5} style={{width: 60}} min={1} step={1}
						value={terrain.overlay_evalChunkBatchSize} onChange={val=>RunInAction_Set(()=>terrain.overlay_evalChunkBatchSize = val)}/>
				</Row>
				<Row mt={5}>
					<Text>Opacity:</Text>
					<Slider ml={5} min={0} max={1} step={0.01}
						value={terrain.overlay_opacity} onChange={val=>RunInAction_Set(()=>terrain.overlay_opacity = val)}/>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Render-chunk LOD threshold (px per sample): a chunk splits into 4 finer chunks when its
						on-screen size exceeds (33 samples × this value) pixels. Lower = sharper terrain (more
						chunks generated). Higher = blurrier but cheaper. 1 = pixel-perfect; 4–6 = typical balanced.
						Independent of the solver fine-cell size above — this only controls the rendered chunk density.
						Applies to BOTH 2d and 3d modes. Default 4.
					`.AsMultiline(0)}>Render-chunk, max px/sample:</TextPlus>
					<Spinner ml={5} style={{width: 60}} min={1} max={32} step={1}
						value={terrain.renderChunk_maxPxPerSample} onChange={val=>RunInAction_Set(()=>terrain.renderChunk_maxPxPerSample = val)}/>
					<Text ml={5}>pixels</Text>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Debug overlay (plan §2.3 ConstraintCellOverlay). Shows the rasterized AMR constraint cells coloured by type:
						Dirichlet (pinned elevation, incl. isoline ring interiors + rims, river/road polylines, spot heights),
						Neumann (slope-pin faces — rendered as highlighted edges on the carrier cell), and Barrier (no-flux cliff cells).
						AMR refinement level is encoded as fill opacity; the per-solve L² residual is shown at the world bbox corner.
						Off by default — enabling it adds a snapshot-build cost to each terrain re-prepare (camera-window-bounded, not per-chunk).
					`.AsMultiline(0)}>Show constraint cells (debug):</TextPlus>
					<CheckBox ml={5} value={terrain.overlay_showConstraintCells}
						onChange={val=>RunInAction_Set(()=>terrain.overlay_showConstraintCells = val)}/>
				</Row>
				{/* ── Mode-specific settings (2d vs 3d) ─────────────────────── */}
				<Row mt={12} style={{fontWeight: "bold"}}>2D mode</Row>
				<Row mt={5}>
					<TextPlus info={`
						The solver's requested fine-cell size (as px per sample), projected to world metres by the
						renderer (divided by worldToScreen) and used as the working-grid / ring-0 solve request.
						Lower = finer solved cells (sharper solved terrain). Higher = coarser but cheaper.
						2D mode only — in 3D the solve cell is pinned to the terrain-definition's mode3D_fineCellSize.
						Default: 4 ≈ 4px per sample.
						Note: Fine-cell-size can end up higher than this, if cell-count would exceed maxWorkingGridLeafCount.
					`.AsMultiline(0)}>Solver fine-cell size:</TextPlus>
					<Spinner ml={5} style={{width: 60}} min={1} max={32} step={1}
						value={terrain.mode2D_solverFineCell_sizeAsPx} onChange={val=>RunInAction_Set(()=>terrain.mode2D_solverFineCell_sizeAsPx = val)}/>
					<Text ml={5}>pixels</Text>
				</Row>
				<Row mt={12} style={{fontWeight: "bold"}}>3D mode</Row>
				<Row mt={5}>
					<TextPlus info={`
						Render-lattice FLOOR (samples per solved fine-cell edge): the render chunk's sample interval
						cannot be finer than (solved cell size ÷ this) metres, so the terrain-chunk width is at
						least ~32 × that. Example: solved fine-cell = 2×2 m and this = 2 → sample interval ≥ 1 m →
						chunk width ≥ ~32 m. Higher = allows a finer lattice (cheaper, bounded depth — this is the
						tree's only depth guard, so it cannot be disabled); lower = coarser lattice. Overrides the
						render-chunk sample max-size above when it binds. 3D perspective mode only (2D lattice is
						already screen-coupled to the solve). Clamped to [0.25, 64]. Default 2.
					`.AsMultiline(0)}>Render-chunk, max samples/fine-cell-edge:</TextPlus>
					<Spinner ml={5} style={{width: 60}} min={0.25} max={64} step={0.25}
						value={terrain.renderChunk_maxSamplesPerFineCellEdge} onChange={val=>RunInAction_Set(()=>terrain.renderChunk_maxSamplesPerFineCellEdge = val)}/>
					<Text ml={5}>samples</Text>
				</Row>
			</>}
		</>
	);
});

const GPUSubpanel = observer_mgl((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const gpu = mapState.settings.gpu;
	// The three options for each per-phase selector row. Mirrors the `ComponentBackend`
	// literal union ("cpu" | "webGL" | "webGPU") declared in
	// `Poisson/Solve/Solve_Common.ts`.
	const backendOptions: ComponentBackend[] = ["cpu", "webGL", "webGPU"];
	return (
		<Column>
			<Row mt={5}>
				<Text style={{fontWeight: "bold"}}>Per-component solver backend selection</Text>
				<Button ml="auto" text="Reset" title="Reset all eight per-phase selectors + the smoother-variant preference to the MapSettings_GPU class defaults (the production fast path: fine/gal/prolong/residual/restrict on WebGPU; galerkinBuild/hierarchy/solve on CPU; wavefront variant)." onClick={()=>{
					RunInAction_Set(()=>{
						/*Object.assign(gpu, new MapSettings_GPU().IncludeKeys(
							"fineSmoother", "galerkinBuild", "galSmoother", "hierarchy",
							"prolong", "residual", "restrict", "solve",
							"preferredSmootherVariant"
						));*/
						Object.assign(gpu, new MapSettings_GPU());
					});
				}}/>
			</Row>
			<Row mt={5}>
				<Text>(set all to...)</Text>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={null} onChange={val=>RunInAction_Set(()=>{
						gpu.fineSmoother = val;
						gpu.galerkinBuild = val;
						gpu.galSmoother = val;
						gpu.hierarchy = val;
						gpu.prolong = val;
						gpu.residual = val;
						gpu.restrict = val;
						gpu.solve = val;
					})}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the fine-level (5-point Laplace) Gauss-Seidel smoother.
					webGPU = the WGSL compute-shader smoother (fastest on desktop Chromium >= 113).
					webGL  = the WebGL2 GLSL-ES-300 fragment-shader smoother (fallback for unstable-WebGPU hosts).
					cpu    = the reference CPU path (byte-faithful; slowest).
				`.AsMultiline(0)}>Fine smoother:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.fineSmoother} onChange={val=>RunInAction_Set(()=>gpu.fineSmoother = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the FIRST-coarsening Galerkin CSR build (buildGalerkinStencil, the
					dominant per-pan cost).  CPU default — the GPU/WebGL ports ship but stay opt-in
					(the GPU shader only handles the first coarsening; inner coarsenings are CPU).
				`.AsMultiline(0)}>Galerkin build:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.galerkinBuild} onChange={val=>RunInAction_Set(()=>gpu.galerkinBuild = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the Galerkin 9-point CSR coarse-level Gauss-Seidel smoother.
					webGPU ships BOTH the lex + wavefront variants (see "Prefer lex smoother variant" below);
					webGL ships wavefront only.
				`.AsMultiline(0)}>Galerkin smoother:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.galSmoother} onChange={val=>RunInAction_Set(()=>gpu.galSmoother = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the coarse-grid topology + GPU/WebGL buffer-pool allocation/upload.
					CPU default — the AMR topology carve-up is intrinsically CPU; this selector controls
					only the buffer pool, falling back to CPU buffers when the backend isn't registered.
				`.AsMultiline(0)}>Hierarchy:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.hierarchy} onChange={val=>RunInAction_Set(()=>gpu.hierarchy = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the prolongation operator (coarse->fine correction transfer).
				`.AsMultiline(0)}>Prolong:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.prolong} onChange={val=>RunInAction_Set(()=>gpu.prolong = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the residual operator (fine->coarse defect computation).
				`.AsMultiline(0)}>Residual:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.residual} onChange={val=>RunInAction_Set(()=>gpu.residual = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the restriction operator (fine->coarse residual transfer).
				`.AsMultiline(0)}>Restrict:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.restrict} onChange={val=>RunInAction_Set(()=>gpu.restrict = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Backend for the coarsest-level direct LU solve (DenseLinear.ts).  CPU default —
					the coarsest direct solve is tiny + intrinsically CPU; the selector is kept for
					symmetry (a future LU-on-GPU port could honour it).
				`.AsMultiline(0)}>Solve:</TextPlus>
				<Select ml="auto" displayType="button bar" options={backendOptions}
					value={gpu.solve} onChange={val=>RunInAction_Set(()=>gpu.solve = val)}/>
			</Row>
			<Row mt={8}>
				<TextPlus style={{flex: 1}} info={`
					Prefer the hyperplane-coloured wavefront coarse smoother variant over the
					lexicographic (per-cell sequential) variant.  Both run on whichever backend
					galSmoother selects.  Lex is the byte-faithful-to-CPU reference; wavefront is
					~64x fewer dispatches on coarse1=4096.  The preference is honoured on backends
					that ship both (currently, all); backends that ship only one (currently, none)
					silently pick the available variant.
				`.AsMultiline(0)}>Prefer wavefront smoother variant:</TextPlus>
				<CheckBox ml={5}
					value={gpu.preferredSmootherVariant === "wavefront"}
					onChange={val=>RunInAction_Set(()=>gpu.preferredSmootherVariant = val ? "wavefront" : "lex")}/>
			</Row>
		</Column>
	);
});

/** Set15 P1 — the 3D-mode view settings (§7). Activation state + active-viewpoint selection live
 *  here (UI state, not DB); the numeric knobs resolve through validated defaults before use (R1). */
const View3DSubpanel = observer_mgl((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const view3D = mapState.settings.view3D;

	// The viewpoint-bearing objects (for the active-viewpoint picker).
	const objects = GetMapObjects(map.id);
	const viewpointObjects = objects.filter(o=>(o.modifiers ?? []).some(m=>m.enabled && m.viewpoint));

	return (
		<Column>
			<Row mt={5}>
				<Text style={{fontWeight: "bold"}}>3D mode (locked viewpoint)</Text>
				<InfoButton ml={5} text={`
					3D mode renders the map from a locked user-placed viewpoint with real terrain relief
					and scenery.  Add a Viewpoint modifier to a MapObject to place one, then select it here
					as the active 3D viewpoint and toggle the mode on (top-left button).
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="3D mode enabled" value={view3D.view3D_enabled}
					onChange={val=>RunInAction_Set(()=>view3D.view3D_enabled = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Which viewpoint object the 3D camera comes from.  Select a MapObject that has a
					Viewpoint modifier.  The camera position = that object's world position + the
					modifier's eye height; orientation = the modifier's yaw/pitch/fov/roll.
				`.AsMultiline(0)}>Active viewpoint:</TextPlus>
				<Select ml="auto" displayType="button bar"
					options={viewpointObjects.map(o=>({name: o.name, value: o.id}))}
					value={view3D.view3D_activeViewpointObjID ?? null}
					onChange={val=>RunInAction_Set(()=>view3D.view3D_activeViewpointObjID = val ?? null)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Far view distance (metres) — the perspective camera's far plane + the fog curve's
					target.  Default = full world extent (terrain reaches the horizon).
				`.AsMultiline(0)}>Max view distance (m):</TextPlus>
				<Spinner ml={5} min={1} step={1000} style={{width: 120}}
					placeholder={""+DEFAULT_MAX_VIEW_DISTANCE}
					value={view3D.maxViewDistance}
					onChange={val=>RunInAction_Set(()=>view3D.maxViewDistance = Math.max(1, val))}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Exponential fog density (1/metres).  Blank = auto-tuned so the horizon fades near
					the max view distance.  Lower = clearer / farther visibility.
				`.AsMultiline(0)}>Fog density:</TextPlus>
				<Spinner ml={5} min={0} step={0.00001} style={{width: 120}}
					value={view3D.fogDensity}
					onChange={val=>RunInAction_Set(()=>view3D.fogDensity = Math.max(0, val))}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Scenery draw distance (metres) in 3D: beyond this distance from the camera, no
					trees are placed.  Terrain reaches the full horizon; scenery cuts off closer.
					Default 5 km.
				`.AsMultiline(0)}>Scenery max distance (m):</TextPlus>
				<Spinner ml={5} step={50} style={{width: 120}}
					placeholder={""+DEFAULT_SCENERY_MAX_DISTANCE}
					value={view3D.view3D_sceneryMaxDistance}
					onChange={val=>RunInAction_Set(()=>view3D.view3D_sceneryMaxDistance = val)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="3D shadows enabled" value={view3D.view3D_shadowsEnabled !== false}
					onChange={val=>RunInAction_Set(()=>view3D.view3D_shadowsEnabled = val)}/>
				<InfoButton ml={5} text={`
					Set15 P8 — directional shadow mapping in 3D.  Terrain + near-tier trees cast; the
					direct (sun) light term is multiplied by the shadow mask.  A one-time render (static
					scene + static light + locked camera) makes this cheap.  The ambient fill is never
					shadowed.
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Near-field shadow-map resolution (px per side).  The locked camera pins where the
					legible near field is, so ONE tight map over it suffices.  Default 2048.
					Note: On most devices, WebGL caps this to a max of 16384 x 16384.
				`.AsMultiline(0)}>Shadow map size (px):</TextPlus>
				<Spinner ml={5} min={128} style={{width: 120}}
					value={view3D.view3D_shadowMapSize} placeholder={""+DEFAULT_SHADOW_MAP_SIZE}
					onChange2={val=>RunInAction_Set(()=>view3D.view3D_shadowMapSize = resolveShadowMapSize<null>(val, null))}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Shadow-camera half-extent (metres) around the eye: the near field the single shadow
					map covers.  Larger = shadows extend further but at lower texel density.  Default 600 m.
				`.AsMultiline(0)}>Shadow radius (m):</TextPlus>
				<Spinner ml={5} style={{width: 120}}
					value={view3D.view3D_shadowRadius} placeholder={""+DEFAULT_SHADOW_RADIUS}
					onChange2={val=>RunInAction_Set(()=>view3D.view3D_shadowRadius = resolveShadowRadius<null>(val, null))}/>
			</Row>
			<Row mt={5}>
				<TextPlus style={{flex: 1}} info={`
					Directional shadow depth-bias.
					* Close to 0 = risks introducing shadow-acne effect. (bits of shadow where it shouldn't be)
					* Far below 0 = makes peter-panning effect worse. (shadows being "offset" from the caster)
					Default -0.0015.
				`.AsMultiline(0)}>Shadow bias:</TextPlus>
				<Spinner ml={5} step={0.0001} style={{width: 120}}
					value={view3D.view3D_shadowBias} placeholder={""+DEFAULT_SHADOW_BIAS}
					onChange2={val=>RunInAction_Set(()=>view3D.view3D_shadowBias = resolveShadowBias<null>(val, null))}/>
			</Row>
			<Row mt={5}>
				<Text style={{fontSize: 12, opacity: 0.7}}>3D solve knobs (near-field radius/cell size, ring
					stack, ring sizes) live on the Terrain Definition's Poisson solver config.</Text>
			</Row>
		</Column>
	);
});